import i18n from '@/i18n'
import { getAtPath } from './configPaths'

function plain(value) {
  return String(value ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\[\[|\]\]|\{\{|\}\}/g, '')
    .replace(/­/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim()
}

function shorten(text, max = 48) {
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text
}

/** The schema field behind an editor path, with its collection context when it has one. */
export function fieldAt(template, path) {
  for (const section of template?.schema?.sections || []) {
    const field = (section.fields || []).find((entry) => entry.path === path)
    if (field) return { field, collection: null, index: null }
  }
  for (const collection of template?.schema?.collections || []) {
    const prefix = `${collection.path}.`
    if (!path?.startsWith(prefix)) continue
    const remainder = path.slice(prefix.length)
    const separator = remainder.indexOf('.')
    if (separator < 0) continue
    const index = Number(remainder.slice(0, separator))
    const relative = remainder.slice(separator + 1)
    for (const section of collection.itemSections || []) {
      const field = (section.fields || []).find((entry) => entry.path === relative)
      if (field) return { field, collection, index }
    }
  }
  return null
}

function valueText(field, value) {
  if (value === null || value === undefined || value === '') return i18n.t('editorV2.assistant.changes.empty')
  if (['select', 'segmented'].includes(field.type)) {
    const option = (field.options || []).find((entry) => entry.value === value)
    return option?.label || String(value)
  }
  if (field.type === 'boolean') return value ? i18n.t('editorV2.assistant.changes.on') : i18n.t('editorV2.assistant.changes.off')
  if (field.type === 'color') return String(value).toUpperCase()
  if (field.type === 'string-list' && Array.isArray(value)) return shorten(value.map(plain).filter(Boolean).join(', '))
  if (field.type === 'duration') return `${value} ms`
  if (typeof value === 'object') return i18n.t('editorV2.assistant.changes.updated')
  return shorten(plain(value))
}

function noun(collection) {
  return collection?.itemLabel || i18n.t(collection?.id === 'slides' ? 'editorV2.assistant.changes.slide' : 'editorV2.assistant.changes.item')
}

/**
 * One truthful line per applied operation, in the editor's own words — "Font Style →
 * Playfair Display", "Slide 2 · Slogan → Summer sale", "Accent → #F2EACE" — so the reply's
 * bullets always match what changed in the form.
 */
export function describeOperations(template, configuration, operations) {
  const slides = (path) => {
    const hit = fieldAt(template, path)
    if (!hit?.collection) return ''
    const count = (getAtPath(configuration, hit.collection.path) || []).length
    return count > 1 ? `${hit.collection.itemLabel || i18n.t('editorV2.assistant.changes.slide')} ${hit.index + 1} · ` : ''
  }
  const lines = []
  for (const operation of operations || []) {
    if (operation.op === 'set_field') {
      const hit = fieldAt(template, operation.path)
      if (operation.path === 'format.id') {
        const format = (template?.manifest?.formats || []).find((entry) => entry.id === operation.value)
        lines.push(i18n.t('editorV2.assistant.changes.adFormat', { format: format?.label || operation.value }))
      } else if (operation.path === 'presetId') {
        lines.push(i18n.t('editorV2.assistant.changes.stylePreset'))
      } else if (hit) {
        lines.push(`${slides(operation.path)}${hit.field.label} → ${valueText(hit.field, operation.value)}`)
      }
      continue
    }
    if (['clear_image', 'focus_image', 'use_image_candidate'].includes(operation.op)) {
      const hit = fieldAt(template, operation.path)
      const label = hit ? `${slides(operation.path)}${hit.field.label}` : i18n.t('editorV2.assistant.changes.image')
      if (operation.op === 'clear_image') lines.push(i18n.t('editorV2.assistant.changes.imageRemoved', { label }))
      if (operation.op === 'focus_image') lines.push(i18n.t('editorV2.assistant.changes.imageFocus', { label }))
      if (operation.op === 'use_image_candidate') lines.push(i18n.t('editorV2.assistant.changes.imageCandidate', { label }))
      continue
    }
    const collection = (template?.schema?.collections || []).find((entry) => entry.id === operation.collectionId)
    const label = noun(collection)
    const item = { label, name: label.toLowerCase() }
    if (operation.op === 'add_collection_item') lines.push(i18n.t('editorV2.assistant.changes.added', item))
    if (operation.op === 'duplicate_collection_item') lines.push(i18n.t('editorV2.assistant.changes.duplicated', item))
    if (operation.op === 'remove_collection_item') lines.push(i18n.t('editorV2.assistant.changes.removed', item))
    if (operation.op === 'move_collection_item') {
      lines.push(i18n.t(operation.direction === 'up' ? 'editorV2.assistant.changes.movedUp' : 'editorV2.assistant.changes.movedDown', item))
    }
  }
  return [...new Set(lines)]
}

const SUBJECTS = [
  [/\bslogans?\b|\bheadlines?\b/i, 'slogan'],
  [/\bbutton\b|\bcta\b|call to action/i, 'button'],
  [/\bphoto|\bimage|\bpicture/i, 'image'],
  [/\blogo|\bfavicon/i, 'logo'],
  [/\blayout\b/i, 'layout'],
  [/\bbubble\b/i, 'bubble'],
  [/\bbackground\b/i, 'background'],
  [/\bslides?\b/i, 'slides'],
]

const ASPECTS = [
  [/\bfont|\bserif|\btypeface|\bitalic|\bbold\b/i, 'font'],
  [/\bcolou?rs?\b|\bpalette|\bwarm|\bdarker|\blighter/i, 'colours'],
  [/\bshorter|\blonger|\bwording|\bcopy\b|\btext\b|\bsounds?\b|\btranslat/i, 'text'],
  [/\bsize|\bbigger|\bsmaller|\blarger/i, 'size'],
  [/\bposition|\balign|\bleft\b|\bright\b|\bcenter|\bcentre/i, 'position'],
]

/**
 * The working line under the thread ("Updating the slogan font…"): named after what the
 * user asked for, so it says what is happening without pretending to know more.
 */
export function workingLabel(request) {
  const text = String(request || '')
  const subjects = SUBJECTS.filter(([pattern]) => pattern.test(text)).map(([, name]) => name)
  const aspects = ASPECTS.filter(([pattern]) => pattern.test(text)).map(([, name]) => name)
  const subject = subjects.find((name) => name !== 'layout')
  const aspect = aspects[0]
  const part = (name) => i18n.t(`editorV2.assistant.working.parts.${name}`)
  if (subject && aspect && !(subject === 'image' && aspect === 'text')) {
    return i18n.t('editorV2.assistant.working.subjectAspect', { subject: part(subject), aspect: part(aspect) })
  }
  if (subject && subjects.includes('layout') && subject !== 'layout') {
    return i18n.t('editorV2.assistant.working.subjectLayout', { subject: part(subject) })
  }
  if (aspect === 'colours' && subjects.includes('layout')) return i18n.t('editorV2.assistant.working.coloursLayout')
  if (subject) return i18n.t('editorV2.assistant.working.part', { part: part(subject) })
  if (subjects.includes('layout')) return i18n.t('editorV2.assistant.working.part', { part: part('layout') })
  if (aspect) return i18n.t('editorV2.assistant.working.part', { part: part(aspect) })
  return i18n.t('editorV2.assistant.working.default')
}
